import React, {useMemo} from 'react';
import {DateTime, DateType} from '@cabinetsbycomputer/datetime';
import {JobCost} from 'components/customer/Job/store/jobApi';
import {isEmpty} from 'lodash';
import {formatPrice} from 'shared/helpers';
import {MinimumUsageFee} from 'shared/components/Header/MinimumUsageFee';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {VariationCost} from 'shared/components/Header/VariationCost';
import {useAppContext} from 'contexts/ApplicationContext';
import {Coupon} from 'shared/Coupon';
import {MinimumChargeMoreInfo} from 'components/customer/Confirmation/Checkout/MinimumChargeMoreInfo';
import {CostingMethod, PriceType} from 'shared/constants/PackagingOption';

interface LegacyCostBreakdownProps {
    cost: JobCost;
    isLoadingCost: boolean;
    isFetchingCost: boolean;
}

// Same price labels as the checkout page: estimates and unpriced options are flagged,
// everything else is a plain formatted amount. Shared with CostBreakdownSimple.
export const getDeliveryExtraPrice = (
    option: JobCost['packagingOptions'][number],
    cost: JobCost
): string => {
    if (!option.has_price) {
        return Number(option.costing_method) === CostingMethod.TO_BE_QUOTED
            ? 'To Be Quoted'
            : 'Price confirmed after review';
    }
    if (
        Number(option.price_type) === PriceType.INDICATIVE &&
        option.needs_confirming
    ) {
        return `${String(formatPrice(option.cost, cost))} (estimate)`;
    }
    return String(formatPrice(option.cost, cost));
};

export const deliveryExtraRows = (
    options: JobCost['packagingOptions'],
    cost: JobCost
): React.ReactNode => {
    if (!options?.length) return null;
    return options.map((option) => (
        <li key={option.id}>
            <span>{option.name}:</span>
            <span>{getDeliveryExtraPrice(option, cost)}</span>
        </li>
    ));
};

export const LegacyCostBreakdown = ({
    cost,
    isLoadingCost,
    isFetchingCost,
}: LegacyCostBreakdownProps) => {
    const {userProfile} = useAppContext();
    const {job} = useJobContext() as PartialJob;

    const hasFreightCost = useMemo(() => {
        return (
            (cost && cost.freightCost > 0) ||
            (cost &&
                cost.couponDetails &&
                cost.couponDetails.discount_free_shipping)
        );
    }, [cost]);

    const showCoupon =
        userProfile.isCouponAvailable ||
        (cost?.coupon > 0 &&
            cost?.couponDetails &&
            !cost.couponDetails.invalid);
    return (
        <>
            {/* coupon available by manufacturer or job has coupon */}
            {showCoupon ? (
                <li>
                    <Coupon />
                </li>
            ) : null}
            <li>
                <span>
                    Job Subtotal
                    {/** job has coupon and coupon not expired */}
                    {cost &&
                    cost.coupon > 0 &&
                    cost.couponDetails &&
                    (isEmpty(cost.couponDetails.expiry_date) ||
                        (!isEmpty(cost.couponDetails.expiry_date) &&
                            DateTime.now().isBefore(
                                DateTime.parse(
                                    cost.couponDetails.expiry_date,
                                    DateType.ISO
                                ).get(),
                                true
                            )))
                        ? ` (Coupon incl.)`
                        : ``}
                    :
                </span>
                <span>
                    {cost && (!isLoadingCost || !isFetchingCost)
                        ? formatPrice(cost.jobSubtotal, cost)
                        : '...'}
                </span>
            </li>

            {cost?.benchtopMaterialUsage?.length > 0 ||
            cost?.cabinetMaterialUsage?.length > 0 ? (
                <MinimumUsageFee />
            ) : null}

            {job.totalProductCount > 0 ? (
                <>
                    {
                        // Minimum charge
                        cost && cost.jobMinChargeFromCustMin > 0 ? (
                            <li>
                                <span style={{display: 'flex'}}>
                                    Minimum Charge Fee:
                                    <MinimumChargeMoreInfo />
                                </span>
                                <span>
                                    {cost && (!isLoadingCost || !isFetchingCost)
                                        ? formatPrice(
                                              cost.jobMinChargeFromCustMin,
                                              cost
                                          )
                                        : '...'}
                                </span>
                            </li>
                        ) : null
                    }
                    <VariationCost />

                    {hasFreightCost ? (
                        <li>
                            <span>
                                Freight{' '}
                                {cost &&
                                cost.couponDetails?.discount_free_shipping
                                    ? '(includes discount)'
                                    : '(minimum cost)'}
                                :
                            </span>
                            <span>
                                {cost && (!isLoadingCost || !isFetchingCost)
                                    ? formatPrice(cost.freightCost, cost)
                                    : '...'}
                            </span>
                        </li>
                    ) : null}

                    {!!cost &&
                        !isLoadingCost &&
                        !isFetchingCost &&
                        deliveryExtraRows(cost.packagingOptions, cost)}

                    <li>
                        <span>{job.countryTaxRateName}</span>
                        <span>
                            {cost && (!isLoadingCost || !isFetchingCost)
                                ? formatPrice(cost.tax, cost)
                                : '...'}
                        </span>
                    </li>
                </>
            ) : null}
            <li className="cost">
                <span>Total Cost:</span>
                <span>
                    {cost && (!isLoadingCost || !isFetchingCost)
                        ? formatPrice(
                              job.totalProductCount > 0 ? cost.totalJobCost : 0,
                              cost
                          )
                        : '...'}
                </span>
            </li>
        </>
    );
};
